import { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelListingDrawer/HotelDetailView'
import type { DrawerHotel } from './HotelListingDrawer'
import {
  applySavedHotelPriceFallback,
  mapOfferDetailToDrawerHotel,
} from './HotelListingDrawer/mapOfferDetailToDrawerHotel'
import { formatDrawerHotelInfo } from './HotelListingDrawer/formatDrawerHotelInfo'
import DrawerAskAIFooter from 'tripPlanner/components/DrawerAskAI/DrawerAskAIFooter'
import useOfferDetails from '../hooks/useOfferDetails'
import useOfferReviews from '../hooks/useOfferReviews'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatMessageActionsContext'
import { isOfferBookedInItinerary, getOfferItineraryDayRange, getOfferSavedBooking } from 'tripPlanner/utils/chatItinerary'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SkeletonWrapper = styled.div`
  padding: ${rem(20)};
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const NarrowLoadingBox = styled(LoadingBox)`
  width: 80%;
`

const ErrorWrapper = styled(Group)`
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

interface Props {
  offerId: string
  accommodationPropertyId?: string
  offerType?: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onShowMap?: (hotel: DrawerHotel) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  searchParams?: OfferClickSearchParams
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
}

function ChatOfferDetailDrawer({
  offerId,
  accommodationPropertyId,
  offerType,
  isOpen,
  onClose,
  onAfterClose,
  onShowMap,
  itineraryData,
  onSendMessage,
  searchParams,
  scrollToRoomOptions,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)

  const { savedBookedRooms, detailOrderId, orderIdForLink } = useOfferBookingState(offerId, itineraryData)

  const { offerDetail, isLoading, isError, refetch } = useOfferDetails({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    offerType,
    accommodationPropertyId,
    checkIn: searchParams?.checkIn,
    checkOut: searchParams?.checkOut,
    occupancies: searchParams?.rooms
      ? JSON.stringify(searchParams.rooms.map(r => ({
          adults: r.adults,
          childrenAge: r.childrenAge ?? [],
        })))
      : undefined,
    flexibleMonths: searchParams?.flexibleMonths,
    flexibleNights: searchParams?.flexibleNights,
    orderId: detailOrderId,
    enabled: isOpen,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!offerDetail,
  })

  const drawerHotel = useMemo(() => {
    if (!offerDetail) return undefined
    let hotel = mapOfferDetailToDrawerHotel(offerDetail)
    hotel = applySavedHotelPriceFallback(hotel, itineraryData)

    // Adjust display price when searchParams dates override the default duration.
    if (searchParams?.checkIn && searchParams?.checkOut && hotel.price) {
      const nights = moment(searchParams.checkOut).diff(moment(searchParams.checkIn), 'days')
      if (nights > 0 && nights !== hotel.price.nights) {
        return { ...hotel, price: { ...hotel.price, nights } }
      }
    }

    return hotel
  }, [offerDetail, searchParams, itineraryData])

  const handleShowMap = useCallback(() => {
    if (drawerHotel) {
      onClose()
      onShowMap?.(drawerHotel)
    }
  }, [onClose, onShowMap, drawerHotel])

  const datesSummary = useMemo(() => {
    if (!searchParams) return undefined
    if (searchParams.checkIn && searchParams.checkOut) {
      const checkIn = moment(searchParams.checkIn)
      const checkOut = moment(searchParams.checkOut)
      return intl.formatMessage(
        {
          id: 'kJGd1p_ChQn6aP6QBw9L',
          defaultMessage: '{checkIn} – {checkOut}',
          description: 'Date range summary label shown in the hotel detail drawer, formatted as check-in through check-out dates',
        },
        {
          checkIn: checkIn.format(DMY_CASUAL_SHORT_FORMAT),
          checkOut: checkOut.format(DMY_CASUAL_SHORT_FORMAT),
        },
      )
    }
    if (searchParams.flexibleMonths || searchParams.flexibleNights) {
      return prettyFlexibleDates(
        searchParams.flexibleMonths,
        searchParams.flexibleNights,
      )
    }
    return undefined
  }, [searchParams, intl])

  const hasGeoLocation = !!drawerHotel?.geoLocation
  const isBooked = isOfferBookedInItinerary(offerId, itineraryData)
  const savedBooking = useMemo(
    () => getOfferSavedBooking(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const filterNights = useMemo(() => {
    if (!searchParams?.checkIn || !searchParams?.checkOut) return undefined
    const nights = moment(searchParams.checkOut).diff(moment(searchParams.checkIn), 'days')
    return nights > 0 ? nights : undefined
  }, [searchParams])

  const itineraryDayLabel = useMemo(() => {
    const range = getOfferItineraryDayRange(offerId, itineraryData)
    return range?.label
  }, [offerId, itineraryData])

  const dateLabel = datesSummary ?? itineraryDayLabel

  const headerActions = useMemo(() => {
    if (!drawerHotel) return undefined
    const showMapLabel = intl.formatMessage({
      id: 'uyDWABuVrlw97fTIbbjL',
      defaultMessage: 'Show map',
      description: 'Button label to show hotel location on map in hotel detail view',
    })
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMap}>
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dateLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">
              {dateLabel}
            </FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={drawerHotel.name}
            offerId={drawerHotel.offerId}
            offerType={drawerHotel.offerType}
            hotelCity={drawerHotel.location}
            hotelCoordinates={drawerHotel.geoLocation}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [drawerHotel, hasGeoLocation, handleShowMap, dateLabel, intl, onSendMessage, isBooked, itineraryData, filterNights])

  const failedToLoadLabel = intl.formatMessage({
    id: 'AyMdUB-fKPPyurSXTes2',
    defaultMessage: 'Failed to load hotel details.',
    description: 'Error message shown when hotel offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading hotel offer details after an error',
  })

  const askAIFooter = drawerHotel ? (
    <DrawerAskAIFooter
      contextLabel={drawerHotel.name}
      isOpen={isOpen}
      hotelDetails={formatDrawerHotelInfo(drawerHotel)}
    />
  ) : undefined

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      headerActions={headerActions}
      footer={askAIFooter}
    >
      {isLoading && (
        <SkeletonWrapper>
          <Group direction="vertical" gap={16}>
            <FullWidthLoadingBox height={200} borderRadius="M" />
            <LoadingBox height={28} width={240} borderRadius="S" />
            <LoadingBox height={20} width={160} borderRadius="S" />
            <FullWidthLoadingBox height={16} borderRadius="S" />
            <NarrowLoadingBox height={16} borderRadius="S" />
          </Group>
        </SkeletonWrapper>
      )}
      {isError && !isLoading && !drawerHotel && (
        <ErrorWrapper direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerHotel && (
        <HotelDetailView
          hotel={drawerHotel}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          isBooked={isBooked}
          orderId={orderIdForLink}
          savedBookedRooms={savedBookedRooms}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          onAfterAction={onClose}
          checkIn={savedBooking?.checkIn ?? searchParams?.checkIn}
          checkOut={savedBooking?.checkOut ?? searchParams?.checkOut}
          initialOccupancies={savedBooking?.occupancies ?? searchParams?.rooms}
          selectedPackageId={savedBooking?.packageId}
          selectedRoomTypeId={savedBooking?.roomTypeId}
          scrollToRoomOptions={scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </TripDrawer>
  )
}

export default ChatOfferDetailDrawer
